import { useCallback, useEffect, useState } from "react";
import type {
  GoodsReceiptDto,
  PurchaseOrderDto,
  StockLocationDto,
} from "@grizzly/shared";
import { purchaseOrderStatus } from "@grizzly/shared";
import { api } from "../../lib/apiClient.js";
import type { ApiClientError } from "../../lib/apiClient.js";
import { formatAmount, formatDateTime } from "../../lib/format.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import { useToast } from "../../lib/useToast.js";
import { ReceiveForm } from "./receiveForm.js";
import { useI18n, type MessageKey } from "../../i18n/index.js";

export function PurchaseOrderPanel({
  orderId,
  locations,
  onChanged,
}: {
  orderId: string;
  locations: StockLocationDto[];
  onChanged(): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [order, setOrder] = useState<PurchaseOrderDto | null>(null);
  const [receipts, setReceipts] = useState<GoodsReceiptDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [receiving, setReceiving] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.getPurchaseOrder(orderId);
      setOrder(res.order);
      setReceipts(res.receipts);
      setError(null);
    } catch (err) {
      setError((err as ApiClientError).message);
    }
  }, [orderId]);

  useEffect(() => {
    void load();
  }, [load]);

  const cancel = async () => {
    setBusy(true);
    try {
      await api.cancelPurchaseOrder(orderId);
      push(t("purchasing.cancelledToast"));
      await load();
      onChanged();
    } catch (err) {
      const e = err as ApiClientError;
      push(e.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  };

  if (error)
    return (
      <p role="alert" className="text-sm text-action-danger">
        {error}
      </p>
    );
  if (!order) return null;

  const canReceive = ["issued", "partially_received"].includes(order.status);
  const canCancel = ["issued", "partially_received"].includes(order.status);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-ink">
            {order.number} · {order.supplierName}
          </h2>
          <p className="text-xs text-ink-muted">
            {order.jobNumber
              ? `${t("purchasing.forJobText", { number: order.jobNumber })} · `
              : ""}
            {t("common.fields.total")} {formatAmount(order.totalMinor)}
            {order.expectedDate
              ? ` · ${t("purchasing.expected", { date: order.expectedDate })}`
              : ""}
          </p>
        </div>
        <Badge tone={purchaseOrderStatus(order.status).tone}>
          {t(`status.purchaseOrder.${order.status}` as MessageKey)}
        </Badge>
      </div>

      <div className="mb-3">
        <h3 className="mb-1 text-xs font-medium text-ink">{t("purchasing.lines")}</h3>
        <ul className="space-y-1">
          {order.lines.map((line) => (
            <li
              key={line.id}
              className="flex items-center justify-between rounded border border-line px-2 py-1 text-sm"
            >
              <span>{line.description}</span>
              <span className="tabular text-ink-muted">
                {t("purchasing.receivedCount", {
                  received: line.quantityReceived,
                  ordered: line.quantityOrdered,
                })}
                {line.outstanding !== "0"
                  ? ` · ${t("purchasing.outstandingCount", { outstanding: line.outstanding })}`
                  : ""}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {receipts.length > 0 ? (
        <div className="mb-3">
          <h3 className="mb-1 text-xs font-medium text-ink">
            {t("purchasing.receipts")}
          </h3>
          <ul className="space-y-1">
            {receipts.map((r) => (
              <li
                key={r.id}
                className="rounded border border-line px-2 py-1 text-xs text-ink-muted"
              >
                {formatDateTime(r.receivedAt)} · {r.receivedByName}
                {r.supplierReference
                  ? ` · ${t("purchasing.ref", { reference: r.supplierReference })}`
                  : ""}
                <span className="ms-2">
                  {r.lines.map((l) => `${l.description} ${l.quantity}`).join(", ")}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex gap-2">
        {canReceive ? (
          <Button size="sm" variant="primary" onClick={() => setReceiving((v) => !v)}>
            {t("purchasing.receive")}
          </Button>
        ) : null}
        {canCancel ? (
          <Button size="sm" variant="danger" disabled={busy} onClick={cancel}>
            {t("common.cancel")}
          </Button>
        ) : null}
      </div>

      {receiving ? (
        <div className="mt-3">
          <ReceiveForm
            order={order}
            locations={locations}
            onReceived={() => {
              setReceiving(false);
              void load();
              onChanged();
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
